import { stripNya, tokenize } from "./display-intent";
import { SOURCE_POINTER_TARGETS } from "./sources";

/** Modul dashboard yang bisa jadi tujuan "tampilkan …": kata kunci + kartu utama (#63). */

export const DISPLAY_MODULE_KEYS = [
	"keuangan",
	"demografi",
	"pengaduan",
	"divisi",
	"bumdes",
	"sosial",
	"keamanan",
	"jenna",
	"beranda",
] as const;
export type DisplayModuleKey = (typeof DISPLAY_MODULE_KEYS)[number];

export interface DisplayModule {
	/** Kata (sudah ternormalisasi) yang menandai modul ini di kalimat. */
	keywords: readonly string[];
	/** Kartu utama yang ditunjuk bila modul dibuka (id registry penunjuk). */
	mainTarget: string;
}

export const DISPLAY_MODULES: Readonly<
	Record<DisplayModuleKey, DisplayModule>
> = {
	keuangan: {
		keywords: [
			"keuangan",
			"apbdes",
			"anggaran",
			"pendapatan",
			"belanja",
			"dana",
			"realisasi",
		],
		mainTarget: "keuangan.kpi-total",
	},
	demografi: {
		keywords: [
			"demografi",
			"penduduk",
			"warga",
			"banjar",
			"pekerjaan",
			"kelahiran",
			"kemiskinan",
		],
		mainTarget: "demografi.kpi-penduduk",
	},
	pengaduan: {
		keywords: ["pengaduan", "aduan", "layanan", "surat", "musrenbang"],
		mainTarget: "pengaduan.kpi-total",
	},
	divisi: {
		keywords: ["divisi", "kinerja", "kegiatan"],
		mainTarget: "divisi.teraktif",
	},
	bumdes: {
		keywords: ["bumdes", "umkm", "omzet", "penjualan", "produk"],
		mainTarget: "bumdes.kpi-umkm-terdaftar",
	},
	sosial: {
		keywords: [
			"sosial",
			"kesehatan",
			"posyandu",
			"stunting",
			"balita",
			"hamil",
		],
		mainTarget: "sosial.kpi-ibu-hamil",
	},
	keamanan: {
		keywords: ["keamanan", "cctv"],
		mainTarget: "keamanan.kpi-cctv-aktif",
	},
	jenna: {
		keywords: ["analytic", "analitik"],
		mainTarget: "jenna.kpi-interaksi",
	},
	beranda: {
		keywords: ["beranda", "sdgs", "kepuasan"],
		mainTarget: "beranda.total-penduduk",
	},
};

const KEYWORD_TO_MODULE: ReadonlyMap<string, DisplayModuleKey> = new Map(
	DISPLAY_MODULE_KEYS.flatMap((key) =>
		DISPLAY_MODULES[key].keywords.map((k) => [k, key] as const),
	),
);

/** Modul pertama yang disebut dalam teks (urutan kemunculan), atau undefined. */
export function detectModule(text: string): DisplayModuleKey | undefined {
	for (const token of tokenize(text)) {
		const key = KEYWORD_TO_MODULE.get(stripNya(token));
		if (key) return key;
	}
	return undefined;
}

/** Modul dari id target registry ("keuangan.kpi-total" → keuangan). */
export function moduleOfTarget(targetId: string): DisplayModuleKey | undefined {
	const prefix = targetId.split(".", 1)[0];
	return DISPLAY_MODULE_KEYS.find((k) => k === prefix);
}

/** Modul dari tool data yang dipakai giliran ini (lewat peta Sumber). */
export function moduleOfDataTool(
	toolsUsed: readonly string[],
): DisplayModuleKey | undefined {
	for (const tool of toolsUsed) {
		const id = SOURCE_POINTER_TARGETS[tool];
		const key = id ? moduleOfTarget(id) : undefined;
		if (key) return key;
	}
	return undefined;
}

/** Modul dari rute halaman yang sedang terbuka; "/" (beranda) tidak dianggap petunjuk. */
export function moduleOfRoute(
	route: string | undefined,
): DisplayModuleKey | undefined {
	const path = (route ?? "").split(/[?#]/, 1)[0] ?? "";
	const map: Record<string, DisplayModuleKey> = {
		"/kinerja-divisi": "divisi",
		"/pengaduan-layanan-publik": "pengaduan",
		"/jenna-analytic": "jenna",
		"/demografi-pekerjaan": "demografi",
		"/keuangan-anggaran": "keuangan",
		"/bumdes": "bumdes",
		"/sosial": "sosial",
		"/keamanan": "keamanan",
	};
	return map[path];
}
